/**
 * Settings → Invoices — edit the company header, contact, warranty terms, and the
 * footer badge that print on customer invoices, with no code. Saves to org_setting
 * key 'invoice_config' (admin-write via RLS); the print view reads it live through
 * useInvoiceConfig(). Install vs Service template specifics land with the Service
 * template build.
 */
import { useEffect, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, ExternalLink } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { InfoTip } from "@/components/InfoTip";
import { useCan } from "@/lib/permissions/PermissionProvider";
import { useOrgSettingJson, useOrgSettingNumber, useSaveOrgSetting } from "@/lib/orgSettings";
import {
  INVOICE_CONFIG_DEFAULTS, INVOICE_CONFIG_KEY, type InvoiceConfig,
} from "@/features/jobs/invoiceConfig";

/** A REAL job to preview against (the link was hardcoded to one UUID that may
 *  not exist — SETTINGS-AUDIT-01). Newest job wins; link hides if none. */
function usePreviewJobId() {
  return useQuery({
    queryKey: ["invoice_preview_job"],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<string | null> => {
      const { data } = await supabase.from("pegasus_job").select("id").order("created_at", { ascending: false }).limit(1);
      return data?.[0]?.id ?? null;
    },
  });
}

export function InvoiceSettingsSection() {
  const { data: stored } = useOrgSettingJson<Partial<InvoiceConfig>>(INVOICE_CONFIG_KEY, {});
  const save = useSaveOrgSetting();
  const canManage = useCan("settings.manage");
  const { data: previewJobId } = usePreviewJobId();
  const [form, setForm] = useState<InvoiceConfig>(INVOICE_CONFIG_DEFAULTS);
  const [saved, setSaved] = useState(false);
  // SETTINGS-NAV-02: payment terms (org_setting payment_terms_days — the due
  // date on invoices and the collections aging) had no screen until now.
  const { data: termsStored } = useOrgSettingNumber("payment_terms_days", 30);
  const qc = useQueryClient();
  const [terms, setTerms] = useState<string | null>(null);
  const termsNum = parseInt(terms ?? "", 10);
  const termsDirty = terms !== null && Number.isFinite(termsNum) && termsNum > 0 && termsNum !== termsStored;

  useEffect(() => { setForm({ ...INVOICE_CONFIG_DEFAULTS, ...(stored ?? {}) }); }, [stored]);

  const set = <K extends keyof InvoiceConfig>(k: K, v: InvoiceConfig[K]) => {
    setForm((f) => ({ ...f, [k]: v })); setSaved(false);
  };
  const onSave = async () => {
    try {
      await save.mutateAsync({ key: INVOICE_CONFIG_KEY, value: form });
      if (termsDirty) {
        await save.mutateAsync({ key: "payment_terms_days", value: termsNum });
        qc.invalidateQueries({ queryKey: ["payment_terms_days"] });
      }
      setTerms(null);
      setSaved(true);
    } catch { /* save.isError shows the message */ }
  };

  return (
    <div>
      <div className="space-y-6 rounded-xl border border-hairline bg-white p-5">
        <Group title="Company" infoLabel="About the invoice logo"
          info={<>The invoice logo is <code>public/sedgwick-logo.png</code>. Drop a new file there to swap it; transparent or white-on-dark reads best on the blue header.</>}>
          <Field label="Company name"><Inp v={form.name} on={(v) => set("name", v)} /></Field>
          <Field label="Tagline"><Inp v={form.tagline} on={(v) => set("tagline", v)} /></Field>
          <Field label="Address line 1"><Inp v={form.addr1} on={(v) => set("addr1", v)} /></Field>
          <Field label="Address line 2"><Inp v={form.addr2} on={(v) => set("addr2", v)} /></Field>
        </Group>

        <Group title="Contact">
          <Field label="Call"><Inp v={form.phone} on={(v) => set("phone", v)} /></Field>
          <Field label="Text"><Inp v={form.textPhone} on={(v) => set("textPhone", v)} /></Field>
          <Field label="Email"><Inp v={form.email} on={(v) => set("email", v)} /></Field>
          <Field label="Website"><Inp v={form.web} on={(v) => set("web", v)} /></Field>
          <Field label="Pay-online URL"><Inp v={form.pay} on={(v) => set("pay", v)} /></Field>
          <Field label="License line"><Inp v={form.license} on={(v) => set("license", v)} /></Field>
        </Group>

        <Group title="Warranty Terms (Years)">
          <Field label="Equipment parts"><NumInp v={form.warrantyPartsEquip} on={(v) => set("warrantyPartsEquip", v)} /></Field>
          <Field label="Controls / thermostat"><NumInp v={form.warrantyPartsControl} on={(v) => set("warrantyPartsControl", v)} /></Field>
          <Field label="Labor"><NumInp v={form.warrantyLabor} on={(v) => set("warrantyLabor", v)} /></Field>
        </Group>

        <Group title="Footer Badge">
          <div className="col-span-2"><Field label="Badge text"><Inp v={form.badge} on={(v) => set("badge", v)} /></Field></div>
        </Group>

        <Group title="Payment Terms" info="Sets the due date on invoices and when a balance counts as past due.">
          <Field label="Due in (days after the invoice)">
            <input type="number" min={1} max={120} value={terms ?? String(termsStored ?? 30)}
              onChange={(e) => { setTerms(e.target.value); setSaved(false); }}
              className="w-full rounded-md border border-hairline px-2.5 py-1.5 text-right text-sm tabular-nums focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30" />
          </Field>
        </Group>

        <div className="flex items-center justify-between gap-2 border-t border-hairline pt-4">
          {previewJobId ? (
            <a href={`/jobs/${previewJobId}/invoice`} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-700 hover:underline">
              <ExternalLink className="h-3.5 w-3.5" /> Preview on the newest job&apos;s invoice
            </a>
          ) : <span className="text-xs text-slate-400">Preview appears once a job exists.</span>}
          <div className="flex items-center gap-3">
            {saved && <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600"><Check className="h-3.5 w-3.5" /> Saved</span>}
            {save.isError && <span className="text-xs font-medium text-rose-600">Didn&apos;t save — try again.</span>}
            {canManage ? (
              <button type="button" onClick={onSave} disabled={save.isPending}
                className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-700 disabled:opacity-50">
                <Check className="h-4 w-4" /> {save.isPending ? "Saving…" : "Save changes"}
              </button>
            ) : (
              <span className="text-xs text-slate-400">Read-only — settings access required to edit.</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Group({ title, info, infoLabel, children }: { title: string; info?: ReactNode; infoLabel?: string; children: ReactNode }) {
  return (
    <section>
      <h4 className="mb-2 flex items-center gap-2 text-[12.5px] font-semibold text-slate-600">
        {title}
        {info && <InfoTip label={infoLabel ?? `About ${title.toLowerCase()}`}>{info}</InfoTip>}
      </h4>
      <div className="grid grid-cols-2 gap-3">{children}</div>
    </section>
  );
}
function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block text-sm">
      <span className="mb-1 block text-[11px] font-medium text-slate-500">{label}</span>
      {children}
    </label>
  );
}
function Inp({ v, on }: { v: string; on: (v: string) => void }) {
  return (
    <input value={v} onChange={(e) => on(e.target.value)}
      className="w-full rounded-md border border-hairline px-2.5 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30" />
  );
}
function NumInp({ v, on }: { v: number; on: (v: number) => void }) {
  return (
    <input type="number" min={0} value={v} onChange={(e) => on(Number(e.target.value) || 0)}
      className="w-full rounded-md border border-hairline px-2.5 py-1.5 text-right text-sm tabular-nums focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30" />
  );
}
